import { Component, computed, effect, forwardRef, inject, Injector, input, model, OnInit, output, viewChild, ChangeDetectionStrategy } from '@angular/core';
import { AbstractControl, ControlValueAccessor, FormControl, FormsModule, NG_VALIDATORS, NG_VALUE_ACCESSOR, NgControl, NgForm, Validator } from '@angular/forms';
import { Subscription } from 'rxjs';
import { LayoutColumnComponent } from 'src/app/controls/components/basic/layout-column/layout-column.component';
import { LayoutRowComponent } from 'src/app/controls/components/basic/layout-row/layout-row.component';
import { TextboxComponent } from 'src/app/controls/components/basic/textbox/textbox.component';
import { NewCommentEvent, PartnerConversation, PartnerOnboardingComment, PartnerOnboardingItemStatus, ToggleConversationEvent } from '../../models/partner-onboarding';
import { OnboardingCommentsIconComponent } from '../onboarding-comments-icon/onboarding-comments-icon.component';
import { OnboardingCommentsContainerComponent } from '../onboarding-comments-container/onboarding-comments-container.component';
import { ConversationComponent } from 'src/app/controls/components/advanced/conversation/conversation.component';
import { Conversation, ConversationMessage } from 'src/app/controls/components/advanced/conversation/models/conversation';

@Component({
  selector: 'app-onboarding-field',
  standalone: true,
  imports: [FormsModule, LayoutRowComponent, LayoutColumnComponent, TextboxComponent, ConversationComponent, OnboardingCommentsContainerComponent, OnboardingCommentsIconComponent],
  templateUrl: './onboarding-field.component.html',
  styleUrl: './onboarding-field.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => OnboardingFieldComponent),
            multi: true
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => OnboardingFieldComponent),
            multi: true
        }
    ],
})
export class OnboardingFieldComponent implements ControlValueAccessor, Validator, OnInit {
    injector = inject(Injector);
    ngControl: NgControl = null;
    form = viewChild<NgForm>('form');
    model = model<unknown>('');
    readonly = input<boolean>(false);
    valueExpr = input.required<string>();
    caption = input.required<string>();
    pattern = input<string | RegExp>('');
    allowConversation = input<boolean>(true);
    readonlyConversation = input<boolean>(false);
    conversation = input<PartnerConversation | null>(null);
    unreadConversationIds = input<string[]>([]);
    autoRefreshEnabled = input<boolean>(false);
    toggleAutoRefreshClicked = output<void>();
    toggleConversationClicked = output<ToggleConversationEvent>();
    newCommentClicked = output<NewCommentEvent>();
    formStatusChanged = output<string>(); // emits: 'VALID' | 'INVALID' | 'PENDING' | 'DISABLED'
    formChangesSubscription: Subscription;

    constructor() {
        effect((onCleanup) => {
            const frm = this.form()?.form;
            if (!frm) return;

            // emit current status once and on subsequent changes
            this.formStatusChanged.emit(frm.status);

            const sub = frm.statusChanges.subscribe(status => this.formStatusChanged.emit(status));
            onCleanup(() => sub.unsubscribe());
        });
    }
    
    ngOnInit(): void {
        this.formChangesSubscription = this.form().form.valueChanges.subscribe(x => {
            const modelValue = this.model();
            const field = this.valueExpr();
            const newModelValue = x as object;
            const value = newModelValue['field'] as string;

            if (modelValue) {
                modelValue[field] = value;
            }
            this.onChanged(modelValue);
        })

        try {
            this.ngControl = this.injector.get(NgControl);
        }
        catch {
            // No NgControl
        }
    }

    conversationToShow = computed(() => {
        if (!this.conversation()) return null;
        return new Conversation(
            {
            Id: this.conversation().Id, 
            UserId: 'partner',
            Comments: this.conversation().Comments?.map(cm => {
                const userId = cm.IsAction ? 'system' : (cm.FromCustomer ? 'customer' : 'partner');
                return new ConversationMessage({UserId: userId, Message: cm.Message, Timestamp: cm.Timestamp})
            }) ?? [], 
            LastRead: this.conversation().PartnerLastRead
            }
        );
    });

    value = computed(() => {
        const modelValue = this.model();
        const field = this.valueExpr();
        return modelValue && typeof modelValue === 'object' && field in modelValue && typeof modelValue[field] === 'string' ? modelValue[field] : null;
    });

    status = computed<PartnerOnboardingItemStatus>(() => {
        const modelValue = this.model();
        const field = 'Status';
        return modelValue && typeof modelValue === 'object' && field in modelValue
            ? modelValue[field] as PartnerOnboardingItemStatus
            : { Status: 'Open' } as PartnerOnboardingItemStatus;
    });

    conversationId = computed<string>(() => {
        const modelValue = this.model();
        const field = 'ConversationId';
        return modelValue && typeof modelValue === 'object' && field in modelValue
            ? modelValue[field] as string
            : null;
    });

    get isRequired(): boolean {
        const control = this.ngControl?.control as FormControl;
        if (!control?.validator) {
            return false;
        }
        const validator = control?.validator({} as AbstractControl);
        return Boolean(validator?.required);
    };
    

    // Conversation methods
    
      hasUnreadMessages(conversationId: string): boolean {
        if (!this.unreadConversationIds()) {
          return false;
        }
        return this.unreadConversationIds()?.some(id => id === conversationId);
      }
    
      toggleConversation(conversationId: string, areaId: string) {
        this.toggleConversationClicked.emit(new ToggleConversationEvent({ConversationId: conversationId, AreaId: areaId}));
      }
    
      newComment(message: ConversationMessage, conversationId: string) {
        const comment = new PartnerOnboardingComment({FromCustomer: false, Message: message.Message, Timestamp: message.Timestamp});
        const newCommentEvent = new NewCommentEvent({Comment: comment, ConversationId: conversationId});
        this.newCommentClicked.emit(newCommentEvent);
      }

      toggleAutoRefresh(): void {
        this.toggleAutoRefreshClicked.emit();
      }

    
    // ControlValueAccessor interface methods
    
    writeValue(obj: unknown): void {
        this.model.set(obj);
    }

    onChanged: (value: unknown) => void = () => { };
    registerOnChange(fn: (value: string) => void): void {
        this.onChanged = fn;
    }

    onTouched: () => void = () => { };
    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState?(isDisabled: boolean): void {
    if (isDisabled) {
        this.form().form.disable();
    } 
    else {
        this.form().form.enable();
    }
    }


    // Validator interface methods

    validate() {
        return this.form().form.valid ? [] : ['Invalid'];
    }
}